import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Chrome, Eye, EyeOff, Gift } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { SEO } from "../components/SEO";
import { FadeUp } from "../components/motion";
import { formatPhone } from "../lib/utils";  // format auto (123) 456-7890 (règle 2026-10-22)

export default function Register() {
  const { setUser } = useAuth();
  const { t, ts } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const refCode = (searchParams.get("ref") || "").trim().toUpperCase();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", password_confirm: "", phone: "", address1: "", city: "", province: "QC", postal_code: "", newsletter: true });  // info-lettre cochée par défaut (demande du propriétaire)
  const [showPwd, setShowPwd] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: k === "phone" ? formatPhone(e.target.value) : e.target.value }));

  useEffect(() => {
    // Suivi des clics sur les liens de référencement (?ref=CODE)
    if (refCode) {
      api.post("/referrals/click", { code: refCode }).catch(() => {});
    }
  }, [refCode]);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // location.state.next : retour à la page d'origine (ex. /commande) pour ne rien perdre
    const redirectUrl = window.location.origin + (location.state?.next || "/compte");
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.password_confirm) {
      setError(t("register.password_mismatch"));
      return;
    }
    setLoading(true);
    try {
      const { password_confirm, ...payload } = form;
      if (refCode) payload.referral_code = refCode;
      const { data } = await api.post("/auth/register", payload);
      setUser(data);
      navigate(location.state?.next || "/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  return (
    <div data-testid="register-page" className="min-h-[80vh] flex items-center justify-center px-6 pt-24 pb-16 spotlight">
      <SEO title={ts("seo.home_title")} description={ts("seo.home_desc")} path="/inscription" noindex />
      <FadeUp className="w-full max-w-md">
        <div className="bg-surface border border-gold/15 rounded-2xl p-10">
          <h1 className="font-serif text-4xl mb-2">{t("register.title")}</h1>
          <p className="text-ivory/70 text-sm mb-8">{t("register.sub")}</p>
          {refCode && (
            <p data-testid="register-ref-note" className="flex items-start gap-2.5 text-sm text-gold border border-gold/30 bg-gold/10 rounded-lg px-4 py-3 mb-5">
              <Gift className="w-4 h-4 mt-0.5 shrink-0" /> {t("referral.note")}
            </p>
          )}
          {error && <p data-testid="register-error" className="text-red-400 text-sm mb-5 border border-red-400/30 bg-red-400/10 rounded-lg px-4 py-3">{error}</p>}
          <form onSubmit={submit} className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-ivory/70 mb-2">{t("register.first_name")} <span className="text-gold">*</span></label>
                <input
                  data-testid="register-first-name" name="given-name" autoComplete="given-name" required value={form.first_name} onChange={set("first_name")}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3.5 text-sm focus:border-gold focus:outline-none transition-colors duration-300"
                />
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-2">{t("register.last_name")} <span className="text-gold">*</span></label>
                <input
                  data-testid="register-last-name" name="family-name" autoComplete="family-name" required value={form.last_name} onChange={set("last_name")}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3.5 text-sm focus:border-gold focus:outline-none transition-colors duration-300"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-ivory/70 mb-2">{t("register.email")} <span className="text-gold">*</span></label>
                <input
                  data-testid="register-email" name="email" autoComplete="email" type="email" required value={form.email} onChange={set("email")}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3.5 text-sm focus:border-gold focus:outline-none transition-colors duration-300"
                />
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-2">{t("register.phone")} <span className="text-gold">*</span></label>
                <input
                  data-testid="register-phone" name="tel" autoComplete="tel" type="tel" required minLength={7} value={form.phone} onChange={set("phone")}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3.5 text-sm focus:border-gold focus:outline-none transition-colors duration-300"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-ivory/70 mb-2">{t("register.address")} <span className="text-gold">*</span></label>
              <input
                data-testid="register-address" name="street-address" autoComplete="street-address" required minLength={3} value={form.address1} onChange={set("address1")}
                className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3.5 text-sm focus:border-gold focus:outline-none transition-colors duration-300"
              />
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-ivory/70 mb-2">{t("register.city")} <span className="text-gold">*</span></label>
                <input
                  data-testid="register-city" name="address-level2" autoComplete="address-level2" required minLength={2} value={form.city} onChange={set("city")}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3.5 text-sm focus:border-gold focus:outline-none transition-colors duration-300"
                />
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-2">{t("register.province")} <span className="text-gold">*</span></label>
                <select
                  data-testid="register-province" value={form.province} onChange={set("province")} required
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3.5 text-sm focus:border-gold focus:outline-none transition-colors duration-300"
                >
                  {Object.entries(t("checkout.provinces")).map(([code, name]) => (
                    <option key={code} value={code}>{name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-2">{t("register.postal")} <span className="text-gold">*</span></label>
                <input
                  data-testid="register-postal" name="postal-code" autoComplete="postal-code" required minLength={6} maxLength={7} value={form.postal_code} onChange={set("postal_code")}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3.5 text-sm focus:border-gold focus:outline-none transition-colors duration-300"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-ivory/70 mb-2">{t("register.password")} <span className="text-gold">*</span></label>
              <div className="relative">
                <input
                  data-testid="register-password" name="new-password" autoComplete="new-password" type={showPwd ? "text" : "password"} required minLength={6} value={form.password} onChange={set("password")}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3.5 pr-12 text-sm focus:border-gold focus:outline-none transition-colors duration-300"
                />
                <button type="button" data-testid="register-password-toggle" onClick={() => setShowPwd((v) => !v)}
                  aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ivory/65 hover:text-gold transition-colors duration-300">
                  {showPwd ? <EyeOff className="w-4.5 h-4.5" /> : <Eye className="w-4.5 h-4.5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm text-ivory/70 mb-2">{t("register.confirm_password")} <span className="text-gold">*</span></label>
              <div className="relative">
                <input
                  data-testid="register-password-confirm" name="new-password" autoComplete="new-password" type={showPwd2 ? "text" : "password"} required minLength={6} value={form.password_confirm} onChange={set("password_confirm")}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3.5 pr-12 text-sm focus:border-gold focus:outline-none transition-colors duration-300"
                />
                <button type="button" data-testid="register-password-confirm-toggle" onClick={() => setShowPwd2((v) => !v)}
                  aria-label={showPwd2 ? "Masquer la confirmation" : "Afficher la confirmation"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ivory/65 hover:text-gold transition-colors duration-300">
                  {showPwd2 ? <EyeOff className="w-4.5 h-4.5" /> : <Eye className="w-4.5 h-4.5" />}
                </button>
              </div>
            </div>
            <label data-testid="register-newsletter" className="flex items-start gap-3 cursor-pointer select-none">
              <input type="checkbox" checked={form.newsletter}
                onChange={(e) => setForm((f) => ({ ...f, newsletter: e.target.checked }))}
                className="mt-1 w-4 h-4 accent-[#d4af37]" />
              <span className="text-sm text-ivory/60 leading-snug">{t("register.newsletter")}</span>
            </label>
            <button
              data-testid="register-submit" type="submit" disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-semibold px-6 py-4 rounded-full hover:bg-champagne transition-colors duration-300 disabled:opacity-60"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} {t("register.submit")}
            </button>
          </form>
          <div className="flex items-center gap-4 my-6">
            <span className="flex-1 border-t border-ivory/10" />
            <span className="text-xs text-ivory/65 uppercase tracking-widest">{t("auth.or")}</span>
            <span className="flex-1 border-t border-ivory/10" />
          </div>
          <button
            type="button"
            data-testid="google-register"
            onClick={googleLogin}
            className="w-full inline-flex items-center justify-center gap-3 border border-ivory/25 text-ivory px-6 py-4 rounded-full hover:border-gold hover:text-gold transition-colors duration-300"
          >
            <Chrome className="w-5 h-5" /> {t("auth.google")}
          </button>
          <p className="text-sm text-ivory/70 mt-6 text-center">
            {t("register.have_account")}{" "}
            <Link to="/connexion" state={location.state} data-testid="goto-login" className="text-gold hover:text-champagne transition-colors duration-300">{t("register.login")}</Link>
          </p>
        </div>
      </FadeUp>
    </div>
  );
}
